import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Verndale Custom Feature */
const mapStateToProps = (state: ApplicationState) => {
    const { canAddToCart } = getSettingsCollection(state).productSettings;
    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        currentLocation: getLocation(state),
        canAddToCart,
    };
};

type Props = ProductCardSelections & HasHistory & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    titleTypography?: TypographyPresentationProps;
    actionsGridItemPriceSignIn?: GridItemProps;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },

    actionsGridItemPriceSignIn: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
            display: inline;
            border: 1px solid;
            margin-top: 10px;
           padding: 15px;
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
            white-space: break-spaces;
        `,
        ellipsis: true,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    userName,
    signInUrl,
    currentUserIsGuest,
    currentLocation,
    history,
    canAddToCart,
}) => {

    const actionsGridItemStyle = canAddToCart ? "" : { ...styles.actionsGridItemPriceSignIn };
    const onSignInHandler = (e: React.MouseEvent) => {
        if (!canAddToCart || currentUserIsGuest) {
            e.preventDefault();
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
    };

    const loginLink = (
        <>
            <Typography>{translate("Please")} </Typography>
            <Link onClick={onSignInHandler} href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}>
                {translate("Sign In")}
            </Link>
            <Typography> {translate("into your account to see price and availability.")}</Typography>
        </>
    );

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`} className="test">
            <StyledWrapper {...styles.imageWrapper}>
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={showCompare}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                />
            </StyledWrapper>
            <ProductListProductInformation
                showAttributes={showAttributes}
                showAvailability={showAvailability}
                showBrand={showBrand}
                showPartNumbers={showPartNumbers}
                showTitle={showTitle}
                showImageBadges={showImageBadges}
                maxImageBadges={maximumNumberOfImageBadges}
                showTextBadges={showTextBadges}
                maxTextBadges={maximumNumberOfTextBadges}
            />
            <StyledWrapper {...actionsGridItemStyle}>
                {canAddToCart && <ProductListActions showAddToList={showAddToList} showPrice={showPrice} />}
                {!canAddToCart && loginLink}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(withProduct(ProductListProductGridCard)));
